import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@features/auth/contexts/AuthContext";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Textarea } from "@shared/components/ui/textarea";
import { Badge } from "@shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Plus, Filter } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shared/components/ui/popover";
import { Calendar } from "@shared/components/ui/calendar";
import { cn } from "@/lib/utils";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import {
  calculateProductStock,
  createStockHistoryRecord,
  bulkCreateStockHistory,
  validateProductStockMovements,
} from "@features/products/services/productService";
import { useStockHistoryInfiniteQuery } from "@features/products/hooks/useStockHistory";

export function StockHistoryManager({ productId }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const queryClient = useQueryClient();

  // Check if user can edit stock movements (admin or manager only)
  const canEditStock = user?.role === "admin" || user?.role === "manager";

  // Helper function to safely translate adjustment types
  const translateAdjustmentType = (type) => {
    const typeMap = {
      manual: t("Manual"),
      pickup: t("Pickup"),
      package: t("Package"),
      correction: t("Correction"),
      import: t("Import"),
      export: t("Export"),
      damage: t("Damage"),
      expiry: t("Expiry"),
      return: t("Return"),
      transfer: t("Transfer"),
    };
    const translated = typeMap[type];
    // Ensure we return a string, fallback to the original type if translation returns an object
    return typeof translated === "string" ? translated : type;
  };

  // Helper function to safely translate reference types
  const translateReferenceType = (type) => {
    const typeMap = {
      pickup: t("Pickup"),
      package: t("Package"),
      invoice: t("Invoice"),
      import: t("Import"),
      transfer: t("Transfer"),
    };
    const translated = typeMap[type];
    // Ensure we return a string, fallback to the original type if translation returns an object
    return typeof translated === "string" ? translated : type;
  };

  // Helper function to safely translate stock health
  const translateStockHealth = (health) => {
    const healthMap = {
      "out of stock": t("Out of Stock"),
      out_of_stock: t("Out of Stock"),
      critical: t("Critical"),
      low: t("Low"),
      moderate: t("Moderate"),
      healthy: t("Healthy"),
    };
    const normalizedHealth = health?.replace("_", " ");
    const translated =
      healthMap[normalizedHealth] || healthMap[health] || t("Unknown");
    // Ensure we return a string, fallback to the original health or "Unknown" if translation returns an object
    return typeof translated === "string" ? translated : health || "Unknown";
  };
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    adjustmentType: "all",
    dateRange: null,
    isValidated: "all",
    referenceType: "all",
  });
  const [formData, setFormData] = useState({
    adjustment: "",
    adjustmentType: "manual",
    reason: "",
    notes: "",
    referenceId: "",
    referenceType: "none",
    location: "default",
    batchNumber: "",
    unitCost: "",
    totalCost: "",
    expiryDate: null,
  });

  // Fetch stock history with infinite scroll
  const {
    data: stockHistoryData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useStockHistoryInfiniteQuery(productId, filters);

  // Transform infinite query data
  const stockHistory = {
    data: stockHistoryData?.pages?.flatMap((page) => page.data) || [],
    total: stockHistoryData?.pages?.[0]?.total || 0,
  };

  // Fetch stock calculation with new method
  const { data: stockCalculation } = useQuery({
    queryKey: ["stockCalculation", productId],
    queryFn: async () => {
      const response = await calculateProductStock(productId, {
        includeDetails: true,
      });
      return response.data;
    },
    enabled: !!productId,
  });

  // Create stock history mutation
  const createStockHistoryMutation = useMutation({
    mutationFn: async (data) => {
      return await createStockHistoryRecord({
        productId,
        ...data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["stockHistory", productId]);
      queryClient.invalidateQueries(["stockCalculation", productId]);
      toast.success(t("products.stockHistoryCreatedSuccessfully"));
      setIsCreateDialogOpen(false);
      setFormData({
        adjustment: "",
        adjustmentType: "manual",
        reason: "",
        notes: "",
        referenceId: "",
        referenceType: "none",
        location: "default",
        batchNumber: "",
        unitCost: "",
        totalCost: "",
        expiryDate: null,
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("products.failedToCreateStockHistory")
      );
    },
  });

  // Bulk create stock history mutation
  const bulkCreateStockHistoryMutation = useMutation({
    mutationFn: async (movements) => {
      return await bulkCreateStockHistory(movements);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["stockHistory", productId]);
      queryClient.invalidateQueries(["stockCalculation", productId]);
      toast.success(t("products.bulkStockHistoryCreatedSuccessfully"));
      setIsBulkDialogOpen(false);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("products.failedToCreateBulkStockHistory")
      );
    },
  });

  // Validate stock movements mutation
  const validateStockMovementsMutation = useMutation({
    mutationFn: async () => {
      return await validateProductStockMovements(productId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["stockHistory", productId]);
      queryClient.invalidateQueries(["stockCalculation", productId]);
      toast.success(
        data.message || t("Stock movements validated successfully")
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("Failed to validate stock movements")
      );
    },
  });

  const handleCreateStockHistory = () => {
    const data = {
      ...formData,
      adjustment: parseFloat(formData.adjustment),
      unitCost: formData.unitCost ? parseFloat(formData.unitCost) : null,
      totalCost: formData.totalCost ? parseFloat(formData.totalCost) : null,
      expiryDate: formData.expiryDate
        ? formData.expiryDate.toISOString()
        : null,
      referenceType:
        formData.referenceType === "none" ? null : formData.referenceType,
    };
    createStockHistoryMutation.mutate(data);
  };

  const getAdjustmentTypeColor = (type) => {
    const colors = {
      manual: "bg-chart-1/10 text-chart-1 border-chart-1/20",
      pickup: "bg-chart-2/10 text-chart-2 border-chart-2/20",
      package: "bg-destructive/10 text-destructive border-destructive/20",
      correction: "bg-chart-4/10 text-chart-4 border-chart-4/20",
      import: "bg-chart-5/10 text-chart-5 border-chart-5/20",
      export: "bg-chart-3/10 text-chart-3 border-chart-3/20",
      damage: "bg-destructive/10 text-destructive border-destructive/20",
      expiry: "bg-muted text-muted-foreground border-border",
      return: "bg-chart-2/10 text-chart-2 border-chart-2/20",
      transfer: "bg-chart-1/10 text-chart-1 border-chart-1/20",
    };
    return colors[type] || "bg-muted text-muted-foreground border-border";
  };

  const formatAdjustment = (adjustment) => {
    const sign = adjustment >= 0 ? "+" : "";
    return `${sign}${adjustment}`;
  };

  const getStockHealthColor = (health) => {
    const colors = {
      healthy: "bg-chart-2/10 text-chart-2 border-chart-2/20",
      moderate: "bg-chart-4/10 text-chart-4 border-chart-4/20",
      low: "bg-chart-3/10 text-chart-3 border-chart-3/20",
      critical: "bg-destructive/10 text-destructive border-destructive/20",
      out_of_stock: "bg-muted text-muted-foreground border-border",
    };
    return colors[health] || "bg-muted text-muted-foreground border-border";
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stock Summary */}
      <div>
        <div className="pb-4 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-2">
            <span className="text-base sm:text-lg font-semibold">
              {t("products.stockSummary")}
            </span>
            <div className="flex flex-col sm:flex-row gap-2">
              {canEditStock && (
                <Dialog
                  open={isCreateDialogOpen}
                  onOpenChange={setIsCreateDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button size="sm" className="text-xs sm:text-sm">
                      <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">
                        {t("Add Movement")}
                      </span>
                      <span className="sm:hidden">{t("Add")}</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-base sm:text-lg">
                        {t("Create Stock Movement")}
                      </DialogTitle>
                      <DialogDescription className="text-sm">
                        {t("Add a new stock movement record")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <Label htmlFor="adjustment" className="text-sm">
                          {t("Adjustment")}
                        </Label>
                        <Input
                          id="adjustment"
                          type="number"
                          value={formData.adjustment}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              adjustment: e.target.value,
                            })
                          }
                          placeholder="Enter adjustment amount"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="adjustmentType" className="text-sm">
                          {t("Type")}
                        </Label>
                        <Select
                          value={formData.adjustmentType}
                          onValueChange={(value) =>
                            setFormData({ ...formData, adjustmentType: value })
                          }
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">
                              {t("Manual")}
                            </SelectItem>
                            <SelectItem value="pickup">
                              {t("Pickup")}
                            </SelectItem>
                            <SelectItem value="package">
                              {t("Package")}
                            </SelectItem>
                            <SelectItem value="correction">
                              {t("Correction")}
                            </SelectItem>
                            <SelectItem value="import">
                              {t("Import")}
                            </SelectItem>
                            <SelectItem value="export">
                              {t("Export")}
                            </SelectItem>
                            <SelectItem value="damage">
                              {t("Damage")}
                            </SelectItem>
                            <SelectItem value="expiry">
                              {t("Expiry")}
                            </SelectItem>
                            <SelectItem value="return">
                              {t("Return")}
                            </SelectItem>
                            <SelectItem value="transfer">
                              {t("Transfer")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="referenceType" className="text-sm">
                          {t("Reference Type")}
                        </Label>
                        <Select
                          value={formData.referenceType}
                          onValueChange={(value) =>
                            setFormData({ ...formData, referenceType: value })
                          }
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue placeholder="Select reference type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t("None")}</SelectItem>
                            <SelectItem value="pickup">
                              {t("Pickup")}
                            </SelectItem>
                            <SelectItem value="package">
                              {t("Package")}
                            </SelectItem>
                            <SelectItem value="invoice">
                              {t("Invoice")}
                            </SelectItem>
                            <SelectItem value="import">
                              {t("Import")}
                            </SelectItem>
                            <SelectItem value="transfer">
                              {t("Transfer")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="referenceId" className="text-sm">
                          {t("Reference ID")}
                        </Label>
                        <Input
                          id="referenceId"
                          value={formData.referenceId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              referenceId: e.target.value,
                            })
                          }
                          placeholder="Reference ID"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reason" className="text-sm">
                          {t("Reason")}
                        </Label>
                        <Input
                          id="reason"
                          value={formData.reason}
                          onChange={(e) =>
                            setFormData({ ...formData, reason: e.target.value })
                          }
                          placeholder="Enter reason for adjustment"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="notes" className="text-sm">
                          {t("Notes")}
                        </Label>
                        <Textarea
                          id="notes"
                          value={formData.notes}
                          onChange={(e) =>
                            setFormData({ ...formData, notes: e.target.value })
                          }
                          placeholder="Additional notes"
                          className="text-sm min-h-[60px]"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label htmlFor="location" className="text-sm">
                            {t("Location")}
                          </Label>
                          <Input
                            id="location"
                            value={formData.location}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                location: e.target.value,
                              })
                            }
                            placeholder="Location"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="batchNumber" className="text-sm">
                            {t("Batch Number")}
                          </Label>
                          <Input
                            id="batchNumber"
                            value={formData.batchNumber}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                batchNumber: e.target.value,
                              })
                            }
                            placeholder="Batch number"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label htmlFor="unitCost" className="text-sm">
                            {t("Unit Cost")}
                          </Label>
                          <Input
                            id="unitCost"
                            type="number"
                            step="0.01"
                            value={formData.unitCost}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                unitCost: e.target.value,
                              })
                            }
                            placeholder="Unit cost"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="totalCost" className="text-sm">
                            {t("Total Cost")}
                          </Label>
                          <Input
                            id="totalCost"
                            type="number"
                            step="0.01"
                            value={formData.totalCost}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                totalCost: e.target.value,
                              })
                            }
                            placeholder="Total cost"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label htmlFor="expiryDate" className="text-sm">
                            {t("Expiry Date")}
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal text-sm",
                                  !formData.expiryDate &&
                                    "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                                {formData.expiryDate
                                  ? format(formData.expiryDate, "PPP")
                                  : t("Select expiry date")}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={formData.expiryDate}
                                onSelect={(date) =>
                                  setFormData({ ...formData, expiryDate: date })
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            onClick={() =>
                              setFormData({ ...formData, expiryDate: null })
                            }
                            className="w-full text-sm"
                          >
                            {t("Clear Date")}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                        className="w-full sm:w-auto text-sm"
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        onClick={handleCreateStockHistory}
                        disabled={createStockHistoryMutation.isPending}
                        className="w-full sm:w-auto text-sm"
                      >
                        {createStockHistoryMutation.isPending
                          ? t("Creating...")
                          : t("Create")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              {canEditStock && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => validateStockMovementsMutation.mutate()}
                  disabled={validateStockMovementsMutation.isPending}
                  className="text-xs sm:text-sm"
                >
                  {validateStockMovementsMutation.isPending
                    ? t("Validating...")
                    : t("Validate")}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="pt-0">
          {stockCalculation && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              <div className="text-center p-3 sm:p-4 rounded-lg bg-muted/30">
                <div className="text-lg sm:text-2xl font-bold">
                  {stockCalculation?.currentStock || 0}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mb-2">
                  {t("Current Stock")}
                </div>
                <Badge
                  className={`text-xs ${getStockHealthColor(
                    stockCalculation?.stockHealth
                  )}`}
                >
                  {translateStockHealth(stockCalculation?.stockHealth)}
                </Badge>
              </div>
              <div className="text-center p-3 sm:p-4 rounded-lg bg-muted/30">
                <div className="text-lg sm:text-2xl font-bold text-chart-2">
                  +{stockCalculation?.totalIn || 0}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t("Total In")}
                </div>
              </div>
              <div className="text-center p-3 sm:p-4 rounded-lg bg-muted/30">
                <div className="text-lg sm:text-2xl font-bold text-destructive">
                  -{stockCalculation?.totalOut || 0}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t("Total Out")}
                </div>
              </div>
              <div className="text-center p-3 sm:p-4 rounded-lg bg-muted/30">
                <div className="text-lg sm:text-2xl font-bold">
                  {stockCalculation?.movementCount || 0}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t("Movements")}
                </div>
              </div>
              <div className="text-center p-3 sm:p-4 rounded-lg bg-muted/30 col-span-2 sm:col-span-1">
                <div className="text-lg sm:text-2xl font-bold text-chart-1">
                  {stockCalculation?.totalValue
                    ? `$${stockCalculation.totalValue.toFixed(2)}`
                    : "-"}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {t("Total Value")}
                </div>
              </div>
            </div>
          )}
          {stockCalculation?.lastMovement && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-xs sm:text-sm text-muted-foreground">
                {t("Last Movement")}:{" "}
                {format(
                  new Date(stockCalculation.lastMovement),
                  "MMM dd, yyyy HH:mm"
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div>
        <div className="pb-4 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-base sm:text-lg font-semibold">
                {t("products.filters")}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setFilters({
                  adjustmentType: "all",
                  dateRange: null,
                  isValidated: "all",
                  referenceType: "all",
                })
              }
              className="text-xs sm:text-sm"
            >
              {t("products.clearFilters")}
            </Button>
          </div>
        </div>
        <div className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <Label className="text-sm">{t("Type")}</Label>
              <Select
                value={filters.adjustmentType}
                onValueChange={(value) =>
                  setFilters({ ...filters, adjustmentType: value })
                }
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder={t("All types")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All types")}</SelectItem>
                  <SelectItem value="manual">{t("Manual")}</SelectItem>
                  <SelectItem value="pickup">{t("Pickup")}</SelectItem>
                  <SelectItem value="package">{t("Package")}</SelectItem>
                  <SelectItem value="correction">{t("Correction")}</SelectItem>
                  <SelectItem value="import">{t("Import")}</SelectItem>
                  <SelectItem value="export">{t("Export")}</SelectItem>
                  <SelectItem value="damage">{t("Damage")}</SelectItem>
                  <SelectItem value="expiry">{t("Expiry")}</SelectItem>
                  <SelectItem value="return">{t("Return")}</SelectItem>
                  <SelectItem value="transfer">{t("Transfer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">{t("Date Range")}</Label>
              <DatePickerWithRange
                onSelectDate={(dateRange) =>
                  setFilters({ ...filters, dateRange })
                }
              />
            </div>
            <div>
              <Label className="text-sm">{t("Validation Status")}</Label>
              <Select
                value={filters.isValidated}
                onValueChange={(value) =>
                  setFilters({ ...filters, isValidated: value })
                }
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder={t("All statuses")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All statuses")}</SelectItem>
                  <SelectItem value="true">{t("Validated")}</SelectItem>
                  <SelectItem value="false">{t("Not validated")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">{t("Reference Type")}</Label>
              <Select
                value={filters.referenceType}
                onValueChange={(value) =>
                  setFilters({ ...filters, referenceType: value })
                }
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder={t("All references")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All references")}</SelectItem>
                  <SelectItem value="pickup">{t("Pickup")}</SelectItem>
                  <SelectItem value="package">{t("Package")}</SelectItem>
                  <SelectItem value="invoice">{t("Invoice")}</SelectItem>
                  <SelectItem value="import">{t("Import")}</SelectItem>
                  <SelectItem value="transfer">{t("Transfer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Stock History Table */}
      <div>
        <div className="pb-4 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-2">
            <span className="text-base sm:text-lg font-semibold">
              {t("products.stockHistory")}
            </span>
            {stockHistory?.total && (
              <Badge
                variant="outline"
                className="text-xs sm:text-sm self-start sm:self-auto"
              >
                {stockHistory.total} {t("movements")}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {t("Complete history of stock movements and adjustments")}
          </p>
        </div>
        <div className="pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">
                    {t("Date")}
                  </TableHead>
                  <TableHead className="text-xs sm:text-sm">
                    {t("Type")}
                  </TableHead>
                  <TableHead className="text-xs sm:text-sm">
                    {t("Adjustment")}
                  </TableHead>
                  <TableHead className="text-xs sm:text-sm">
                    {t("Running Total")}
                  </TableHead>
                  <TableHead className="text-xs sm:text-sm hidden md:table-cell">
                    {t("Reference")}
                  </TableHead>
                  <TableHead className="text-xs sm:text-sm hidden lg:table-cell">
                    {t("Reason")}
                  </TableHead>
                  <TableHead className="text-xs sm:text-sm hidden xl:table-cell">
                    {t("User")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockHistory?.data?.map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell className="text-xs sm:text-sm whitespace-nowrap">
                      <div className="md:hidden">
                        {format(new Date(movement.createdAt), "dd/MM/yy")}
                      </div>
                      <div className="hidden md:block">
                        {format(
                          new Date(movement.createdAt),
                          "MMM dd, yyyy HH:mm"
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-xs ${getAdjustmentTypeColor(
                          movement.adjustmentType
                        )}`}
                      >
                        <span className="md:hidden">
                          {translateAdjustmentType(
                            movement.adjustmentType
                          ).slice(0, 3)}
                        </span>
                        <span className="hidden md:inline">
                          {translateAdjustmentType(movement.adjustmentType)}
                        </span>
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={`text-xs sm:text-sm font-medium ${
                        movement.adjustment >= 0
                          ? "text-chart-2"
                          : "text-destructive"
                      }`}
                    >
                      {formatAdjustment(movement.adjustment)}
                    </TableCell>
                    <TableCell className="text-xs sm:text-sm font-medium">
                      {movement.runningTotal || movement.newStock || "-"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {movement.referenceId ? (
                        <div className="text-xs sm:text-sm">
                          <div className="font-medium truncate max-w-20 lg:max-w-32">
                            {movement.referenceId}
                          </div>
                          {movement.referenceType && (
                            <div className="text-muted-foreground">
                              {translateReferenceType(movement.referenceType)}
                            </div>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell max-w-32 truncate text-xs sm:text-sm">
                      {movement.reason}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      <div className="flex items-center gap-2">
                        {movement.user?.avatar && (
                          <img
                            src={movement.user.avatar}
                            alt={
                              movement.user.name ||
                              movement.user.brandName ||
                              t("Unknown")
                            }
                            className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate text-xs sm:text-sm max-w-24">
                            {movement.user?.name ||
                              movement.user?.brandName ||
                              t("Unknown User")}
                          </div>
                          {movement.user?.email && (
                            <div className="text-xs text-muted-foreground truncate max-w-24">
                              {movement.user.email}
                            </div>
                          )}
                          {movement.user?.role && (
                            <div className="text-xs text-muted-foreground capitalize">
                              {movement.user.role}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Infinite Scroll Trigger */}
          <InfiniteScrollTrigger
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          />

          {(!stockHistory?.data || stockHistory.data.length === 0) && (
            <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
              {t("products.noStockHistoryFound")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
